import React from 'react';
import { navigate } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  brandFonts,
  fontSize,
  standardTransition,
} from '../styles';
import { Container, Heading, Link } from './ui';
import { buildUrl } from '../utils';

const StyledImageLinkCards = styled.section`
  ${sectionMargins()};
`;

const StyledHeading = styled(Heading)`
  text-align: center;
`;

const StyledItems = styled.div`
  display: grid;
  gap: 20px;

  ${minBreakpointQuery.tiny`
    gap: 30px;
    margin-top: 10px;
  `}

  ${minBreakpointQuery.tsmall`
    grid-template-columns: repeat(6, 1fr);
  `}

  ${minBreakpointQuery.small`
    gap: 40px;
  `}

  ${minBreakpointQuery.large`
    gap: 50px;
    margin-top: 20px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 60px;
  `}
`;

// moved up for hover
const StyledImage = styled(GatsbyImage)`
  max-height: 320px;
  ${standardTransition('transform')};

  ${minBreakpointQuery.small`
    max-height: 340px;
  `}

  ${minBreakpointQuery.large`
    max-height: 360px;
  `}
`;

const StyledItem = styled.article`
  position: relative;
  overflow: hidden;
  cursor: pointer;

  ${minBreakpointQuery.tsmall`
    grid-column: span 3;
  `}

  ${({ itemsAmount }) => {
    if (itemsAmount === 3 || itemsAmount > 4) {
      return css`
        &:nth-child(-n + 3) {
          ${minBreakpointQuery.medium`
            grid-column: span 2;
          `}
        }
      `;
    }
  }}

  ${({ itemsAmount }) => {
    if (itemsAmount > 5 && itemsAmount % 2 === 0) {
      return css`
        &:nth-last-child(-n + 3) {
          ${minBreakpointQuery.medium`
            grid-column: span 2;
          `}
        }
      `;
    }
  }}

  &:hover {
    ${StyledImage} {
      transform: scale(1.05);
    }
  }
`;

const StyledContent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: end;
  padding: 25px 20px;
  color: ${standardColours.white};
  background-color: ${standardColours.transparentBlack(0.4)};

  ${minBreakpointQuery.large`
    padding: 30px 25px;
  `}
`;

const StyledSubheading = styled.h3`
  font-family: ${brandFonts.secondary};
  ${fontSize(20)};

  ${minBreakpointQuery.large`
    ${fontSize(22)};
  `}
`;

const StyledText = styled.p`
  margin-top: 6px;
  ${fontSize(14)};

  ${minBreakpointQuery.large`
    ${fontSize(15)};
  `}
`;

const ImageLinkCards = ({ heading, items }) => (
  <StyledImageLinkCards>
    <Container>
      <StyledHeading>{heading}</StyledHeading>
      <StyledItems>
        {items.map(({ image, link, text }) => (
          <StyledItem
            itemsAmount={items.length}
            onClick={() => {
              navigate(buildUrl(link.page.slug, link.page));
            }}
          >
            <StyledImage image={image.gatsbyImageData} alt={image.alt} />
            <StyledContent>
              <StyledSubheading>
                <Link to={link.page.slug} linkData={link.page}>
                  {link.text}
                </Link>
              </StyledSubheading>
              {text && <StyledText>{text}</StyledText>}
            </StyledContent>
          </StyledItem>
        ))}
      </StyledItems>
    </Container>
  </StyledImageLinkCards>
);

export default ImageLinkCards;
